import type { Speaker, Track } from "../event-experience/schemas";

import { cleanValue } from "./announcement-placeholders";
import type { AttendeeRecord } from "./use-attendee-records";

/**
 * A track champion (TNG-95) is a link to a roster attendee or a speaker. Its
 * name is looked up when it is shown, the way the backend looks it up when an
 * announcement goes out (firestore_announcement_recipients.py), so both say
 * the same thing about the same track.
 */

/** Whose roster row still stands for somebody; attendee_projection.py. */
const ROSTER_STATUSES = new Set(["pending", "confirmed"]);

export function isOnRoster(attendee: Pick<AttendeeRecord, "status">): boolean {
  return ROSTER_STATUSES.has(attendee.status ?? "");
}

export type ChampionLink =
  { kind: "attendee"; id: string } | { kind: "speaker"; id: string };

/** The fields a track keeps its champions in: the first, then the second. */
export type TrackChampionFields = Pick<
  Track,
  | "championAttendeeId"
  | "championSpeakerId"
  | "secondChampionAttendeeId"
  | "secondChampionSpeakerId"
>;

/**
 * A stored link id as the backend reads it (firestore_announcement_recipients
 * `_text`): cleaned like a placeholder value, and absent when blank or when it
 * holds a "/", which would name a path rather than a document.
 */
function linkId(value: string | undefined): string | null {
  const id = cleanValue(value);
  return id && !id.includes("/") ? id : null;
}

function linkOf(
  attendeeId: string | undefined,
  speakerId: string | undefined,
): ChampionLink | null {
  const attendee = linkId(attendeeId);
  if (attendee) return { kind: "attendee", id: attendee };
  const speaker = linkId(speakerId);
  if (speaker) return { kind: "speaker", id: speaker };
  return null;
}

/** The first champion's link, or null. */
export function championLink(
  track: Pick<Track, "championAttendeeId" | "championSpeakerId">,
): ChampionLink | null {
  return linkOf(track.championAttendeeId, track.championSpeakerId);
}

/** The second champion's link, or null. */
export function secondChampionLink(
  track: Pick<Track, "secondChampionAttendeeId" | "secondChampionSpeakerId">,
): ChampionLink | null {
  return linkOf(track.secondChampionAttendeeId, track.secondChampionSpeakerId);
}

/**
 * Both champion links, first then second, each at most once: a track that
 * names the same person twice has one champion.
 */
export function championLinks(track: TrackChampionFields): ChampionLink[] {
  const links: ChampionLink[] = [];
  for (const link of [championLink(track), secondChampionLink(track)]) {
    if (link && !links.some((l) => l.kind === link.kind && l.id === link.id))
      links.push(link);
  }
  return links;
}

/** One link's current name, or null when it points at nobody in the event. */
export function linkName(
  link: ChampionLink,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string | null {
  if (link.kind === "attendee") {
    const row = attendees.get(link.id);
    return row && isOnRoster(row) ? cleanValue(row.name) : null;
  }
  return cleanValue(speakers.get(link.id)?.name);
}

/**
 * Who a link stands for. A speaker with `attendeeId` is the same person as
 * that roster row, so a track that names the row and the speaker, or two
 * speakers for the same row, has one champion.
 */
export function personKey(
  link: ChampionLink,
  speakerAttendeeId: string | undefined,
): string {
  if (link.kind === "attendee") return `attendee:${link.id}`;
  const attendeeId = linkId(speakerAttendeeId);
  return attendeeId ? `attendee:${attendeeId}` : `speaker:${link.id}`;
}

export interface ResolvedChampions {
  /** The champions' names as they read now, first then second, each person once. */
  names: string[];
  /** Links that point at nobody in the event any more. */
  stale: number;
}

/**
 * The track's champions as they read now. A link that points at someone no
 * longer in the event (row deleted or withdrawn, speaker deleted) counts as
 * stale. A link to a person already named counts as neither. The backend
 * resolves the same way (firestore_announcement_recipients.py), and both run
 * the shared vectors in
 * functions/python/tests/fixtures/announcement_placeholders/champion_vectors.json.
 */
export function resolveChampions(
  track: TrackChampionFields,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): ResolvedChampions {
  const names: string[] = [];
  const named = new Set<string>();
  let stale = 0;
  for (const link of championLinks(track)) {
    const key = personKey(
      link,
      link.kind === "speaker" ? speakers.get(link.id)?.attendeeId : undefined,
    );
    if (named.has(key)) continue;
    const name = linkName(link, attendees, speakers);
    if (name === null) {
      stale += 1;
      continue;
    }
    named.add(key);
    names.push(name);
  }
  return { names, stale };
}

/** The names of the track's champions as they read now; see resolveChampions. */
export function championNames(
  track: TrackChampionFields,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string[] {
  return resolveChampions(track, attendees, speakers).names;
}

/**
 * What {champion} says: both names joined with " and ", one name alone, or
 * null when the track has none or every link is stale. A personalised
 * announcement then says "TBA" or its fallback. The backend joins the same
 * way (firestore_announcement_recipients.py).
 */
export function championName(
  track: TrackChampionFields,
  attendees: ReadonlyMap<string, AttendeeRecord>,
  speakers: ReadonlyMap<string, Speaker>,
): string | null {
  const names = championNames(track, attendees, speakers);
  return names.length > 0 ? names.join(" and ") : null;
}
